import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, fmtMoneyShort, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// What actually earns: invoice lines grouped by item (medicines, services, extras) plus OPD
// consultation fees by medical service.

const ITEM_TYPE_LABEL = {
  MEDICINE: 'Medicine',
  MEDICAL_SERVICE: 'Service',
  EXTRA_SERVICE: 'Extra',
};

const ITEM_COLUMNS = [
  { key: 'name', label: 'Item' },
  { key: 'itemType', label: 'Type', render: (r) => ITEM_TYPE_LABEL[r.itemType] || r.itemType, csv: (r) => ITEM_TYPE_LABEL[r.itemType] || r.itemType },
  { key: 'qty', label: 'Qty', align: 'right' },
  { key: 'gross', label: 'Gross', align: 'right', render: (r) => fmtMoney(r.gross) },
  { key: 'discount', label: 'Discount', align: 'right', render: (r) => fmtMoney(r.discount) },
  { key: 'net', label: 'Net', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.net)}</span>, csv: (r) => r.net },
  { key: 'invoices', label: 'Invoices', align: 'right' },
];

const OPD_COLUMNS = [
  { key: 'service', label: 'Service' },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'net', label: 'Fees (net)', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.net)}</span>, csv: (r) => r.net },
];

const RevenueByServiceReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [typeFilter, setTypeFilter] = useState('ALL');

  const fetcher = useCallback(
    () => reportsAPI.getRevenueByService({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const items = useMemo(() => {
    const all = data?.invoiceItems || [];
    return typeFilter === 'ALL' ? all : all.filter((r) => r.itemType === typeFilter);
  }, [data, typeFilter]);

  const top10 = useMemo(
    () => items.slice(0, 10).map((r) => ({ name: r.name, net: Number(r.net) })),
    [items]
  );

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {['ALL', 'MEDICINE', 'MEDICAL_SERVICE', 'EXTRA_SERVICE'].map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                typeFilter === t ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t === 'ALL' ? 'All items' : `${ITEM_TYPE_LABEL[t]}s`}
            </button>
          ))}
        </div>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <ChartPanel title="Top 10 earners (invoice items, net)">
        {loading || top10.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No invoice items in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, top10.length * 34)}>
            <BarChart data={top10} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
              <YAxis type="category" dataKey="name" {...axisProps} width={170} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="net" radius={[0, 4, 4, 0]} barSize={18}>
              {top10.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'service', value: r.name, from: range.from, to: range.to })}
        title="Invoice revenue by item"
        columns={ITEM_COLUMNS}
        rows={items}
        csvName={`revenue-by-item-${range.from}-to-${range.to}`}
      />
      <ReportTable
        onRowClick={(r) => drill.open({ type: 'service', value: r.service, from: range.from, to: range.to })}
        title="OPD consultation fees by service"
        columns={OPD_COLUMNS}
        rows={data?.opdServices || []}
        csvName={`opd-by-service-${range.from}-to-${range.to}`}
        emptyText="No visits in the selected range"
      />

      <ReportPrintLayout
        title="Revenue by Service & Item"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[
          { title: 'Invoice revenue by item', columns: ITEM_COLUMNS, rows: items },
          { title: 'OPD consultation fees by service', columns: OPD_COLUMNS, rows: data?.opdServices || [] },
        ]}
      />

      {drill.node}
    </div>
  );
};

export default RevenueByServiceReport;
